import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { IMAGES, VALUES, DIFFERENTIALS } from "../lib/content";

export default function QuemSomos() {
  return (
    <>
      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-20 md:pb-28 bg-white" data-testid="quem-somos-hero">
        <div className="sca-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <Reveal className="lg:col-span-7">
            <p className="sca-overline mb-6">[ Quem somos ]</p>
            <h1 className="sca-h1 max-w-[18ch]">
              A engenharia por trás de obras <span className="sca-serif text-sca-red">organizadas, seguras e previsíveis</span>.
            </h1>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <p className="sca-body">
              A SCA Construtora é especializada na execução e gestão de obras industriais, comerciais e
              residenciais de médio e alto padrão. Fundada a partir da experiência prática em grandes obras,
              a empresa consolidou sua atuação com foco em organização técnica, planejamento rigoroso e
              execução com responsabilidade.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Image band — Fachada da sede SCA Construtora */}
      <section className="relative">
        <Reveal>
          <div className="relative overflow-hidden h-[60vh] md:h-[78vh] bg-sca-mist">
            <img
              src={IMAGES.fachada}
              alt="Fachada da sede da SCA Construtora"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <div className="absolute bottom-8 left-6 md:bottom-12 md:left-12 max-w-2xl text-white">
              <p className="sca-overline text-white/80 mb-4">[ Sede SCA · Piracicaba/SP ]</p>
              <p className="font-sans text-xl md:text-2xl lg:text-3xl font-medium leading-snug">
                A SCA nasceu para mostrar que <span className="sca-serif italic text-sca-red">obra séria</span> exige
                método, engenharia aplicada e controle técnico.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Diferenciais */}
      <section id="diferenciais" className="sca-section bg-sca-mist">
        <div className="sca-container">
          <Reveal>
            <p className="sca-overline mb-6">[ Diferenciais ]</p>
            <h2 className="sca-h2 max-w-3xl mb-14">
              O que diferencia <span className="sca-serif text-sca-red">a SCA</span>.
            </h2>
          </Reveal>

          <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
            {DIFFERENTIALS.map((d) => (
              <StaggerItem key={d.title} className="flex gap-5 py-5 border-b border-sca-line">
                <Check className="w-6 h-6 text-sca-red flex-shrink-0 mt-1" strokeWidth={2.5} />
                <div>
                  <h3 className="font-sans text-lg md:text-xl font-semibold text-sca-black mb-1.5">{d.title}</h3>
                  <p className="text-sm md:text-base text-sca-steel leading-relaxed">{d.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Missão Visão Valores */}
      <section id="mvv" className="sca-section bg-white">
        <div className="sca-container">
          <Reveal>
            <p className="sca-overline mb-6">[ Missão · Visão · Valores ]</p>
            <h2 className="sca-h2 mb-16 max-w-3xl">
              Os princípios que orientam <span className="sca-serif text-sca-red">cada decisão</span>.
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-sca-line border-t border-l border-sca-line">
            <Reveal className="bg-white p-8 md:p-10 border-r border-b border-sca-line">
              <span className="font-mono text-xs tracking-[0.28em] text-sca-red font-bold">M / 01</span>
              <h3 className="font-sans text-2xl md:text-3xl font-semibold text-sca-black mt-6 mb-5">Missão</h3>
              <p className="sca-body">
                Executar obras com excelência técnica, planejamento rigoroso e responsabilidade.
              </p>
            </Reveal>
            <Reveal delay={0.1} className="bg-white p-8 md:p-10 border-r border-b border-sca-line">
              <span className="font-mono text-xs tracking-[0.28em] text-sca-red font-bold">V / 02</span>
              <h3 className="font-sans text-2xl md:text-3xl font-semibold text-sca-black mt-6 mb-5">Visão</h3>
              <p className="sca-body">
                Consolidar-se como referência regional em obras industriais e comerciais.
              </p>
            </Reveal>
            <Reveal delay={0.2} className="bg-sca-black text-white p-8 md:p-10 border-r border-b border-sca-line">
              <span className="font-mono text-xs tracking-[0.28em] text-sca-red font-bold">V / 03</span>
              <h3 className="font-sans text-2xl md:text-3xl font-semibold mt-6 mb-5">Valores</h3>
              <ul className="space-y-2.5">
                {VALUES.map((v) => (
                  <li key={v} className="flex items-center gap-3 text-sm md:text-base text-white/85">
                    <span className="w-1.5 h-1.5 bg-sca-red" />
                    {v}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="sca-section bg-sca-mist">
        <div className="sca-container grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <Reveal className="md:col-span-8">
            <h3 className="font-sans text-3xl md:text-5xl font-bold leading-tight tracking-tightest">
              Conheça a metodologia que <span className="sca-serif text-sca-red">estrutura cada obra</span>.
            </h3>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-4 md:flex md:justify-end">
            <Link to="/metodologia-de-obra" className="btn-sca-dark" data-testid="quem-somos-cta-metodologia">
              Ver metodologia <ArrowUpRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
